import { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import AssetPreview from 'src/components/asset-preview';
import { EldDriverStatusLabel } from 'src/components/eld-driver-status-label';
import { Violations } from 'src/components/violations';
import { DeviceWithMapInfo, EldEngineImmobilizer, EldVehicle, EldDriverLogGroup, EldVehicleStats, DeviceEldDriver } from 'src/models';
import { formatDatetimeForPreview, formatTimeAsHourMin } from 'src/utils/date-utils';
import { overSpeed, getDeviceDrivers, getLabelFromOrigin } from 'src/utils/device-utils';
import { formatDtc, formatAtMost2Decimals } from '../eld/eld-vehicles/_eld_vehicle_modal';

export type VehicleInfoProps = {
  deviceEldDrivers: DeviceEldDriver[],
  selectedDevice: DeviceWithMapInfo;
  handleShareLocationClick: (vehicle?: DeviceWithMapInfo) => void;
  onClose: () => void;
  timezone: string;
  eldEngineImmobilizer?: EldEngineImmobilizer;
  eldVehicles: EldVehicle[];
  setDeviceForHistory: (device: DeviceWithMapInfo) => void;
  setViewingMode: (mode: "sidebar" | "history") => void;
  eldDriverLogGroups?: EldDriverLogGroup[];
  eldVehicleStats?: EldVehicleStats[];
};

export default function VehicleInfo({
  deviceEldDrivers,
  selectedDevice,
  handleShareLocationClick,
  onClose,
  timezone,
  eldEngineImmobilizer,
  eldVehicles,
  setDeviceForHistory,
  setViewingMode,
  eldDriverLogGroups,
  eldVehicleStats,
}: VehicleInfoProps) {
  const [sensorsExpanded, setSensorsExpanded] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(true);
  if (!selectedDevice) {
    return null;
  }

  const device = selectedDevice;


  const findVehicleInfo = () => {
    if (!eldVehicles || eldVehicles.length === 0) return null;
    const matchedVehicle = eldVehicles.find(vehicle => vehicle.deviceId === device.id);
    return matchedVehicle || null;
  };

  const vehicleInfo = findVehicleInfo();
  const vehicleVin = vehicleInfo?.vin;

  const currentVehicleStats = eldVehicleStats?.find(
    stats => vehicleInfo && stats.eldVehicleId === vehicleInfo.id
  ) || null;

  const getDriverLogGroup = (driverId: number) => eldDriverLogGroups?.find(log => log.eldDriverId === driverId);

  const isTruck = device.deviceType.startsWith("ELD Truck");

  const eldDriversForDevice = getDeviceDrivers(deviceEldDrivers, device);
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 1,
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#f8f9fa",
        minHeight: "48px"
      }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
          Vehicle Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Iconify icon="solar:arrow-left-linear" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <Box sx={{ textAlign: "center", p: { xs: 1, md: 1.5 }, borderBottom: "1px solid #e0e0e0" }}>
          <AssetPreview device={device} />

          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5, fontSize: { xs: "14px", md: "16px" } }}>
            {device.deviceType}
          </Typography>

          {vehicleInfo && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: "10px", md: "11px" }, lineHeight: 1.3 }}>
              <strong>Vehicle:</strong> {vehicleInfo.vehicleUnitNumber}
              {vehicleInfo.vehicleMake && vehicleInfo.vehicleModel && (
                <br />
              )}
              {vehicleInfo.vehicleMake && vehicleInfo.vehicleModel && (
                <span>{vehicleInfo.vehicleMake} {vehicleInfo.vehicleModel}</span>
              )}
              {vehicleInfo.vehicleYear && (
                <span> ({vehicleInfo.vehicleYear})</span>
              )}
            </Typography>
          )}

          {vehicleInfo?.licensePlate && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: "10px", md: "11px" } }}>
              <strong>License:</strong> {vehicleInfo.licensePlate}
              {vehicleInfo.licensePlateState && (
                <span> ({vehicleInfo.licensePlateState})</span>
              )}
            </Typography>
          )}

          <Stack spacing={{ xs: 0.75, md: 1 }} sx={{ px: { xs: 0.5, md: 0 }, mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="ic:baseline-share" />}
              onClick={() => {
                handleShareLocationClick(device);
              }}
              fullWidth
              size="small"
              sx={{ fontSize: { xs: "11px", md: "12px" }, height: { xs: "30px", md: "32px" }, "&:hover": { backgroundColor: "#aadb26" } }}
            >
              Live Share
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="mdi:history" />}
              onClick={() => {
                  setDeviceForHistory(device);
                  setViewingMode("history");
              }}
              fullWidth
              size="small"
              sx={{ fontSize: { xs: "11px", md: "12px" }, height: { xs: "30px", md: "32px" }, "&:hover": { backgroundColor: "#aadb26" } }}
            >
              Device History
            </Button>
          </Stack>
        </Box>

        {isTruck && (
           <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
            <Box sx={{ p: { xs: 1, md: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ fontSize: { xs: "13px", md: "14px" }, fontWeight: "600", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                  <Iconify icon="mdi:account-group" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                  Drivers ({eldDriversForDevice.length || 0})
                </Box>
              </Typography>
              {(!eldDriversForDevice || eldDriversForDevice.length === 0) ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                  No drivers assigned to this vehicle
                </Typography>
              ) : (
                <List disablePadding>
                  {eldDriversForDevice.map((driverItem: any, index: number) => {
                    const driverLogGroup = getDriverLogGroup(driverItem.id);
                    return (
                      <Box key={driverItem.id || index}>
                        <ListItem disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>
                                    {driverItem.firstName && driverItem.lastName
                                      ? `${driverItem.firstName} ${driverItem.lastName}`
                                      : driverItem.name || 'Unknown Driver'
                                    }
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {driverLogGroup?.lastStatus && (
                                      <EldDriverStatusLabel
                                        status={driverLogGroup.lastStatus}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                {driverLogGroup && (
                                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.5, mt: 0.5 }}>
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                        BREAK
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                        {formatTimeAsHourMin(driverLogGroup.breakTime)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                        DRIVE
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                        {formatTimeAsHourMin(driverLogGroup.driveTime)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                        SHIFT
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                        {formatTimeAsHourMin(driverLogGroup.shiftTime)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "9px", display: "block" }}>
                                        CYCLE
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: "10px", fontWeight: "600" }}>
                                        {formatTimeAsHourMin(driverLogGroup.cycleTime)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < (eldDriversForDevice?.length || 0) - 1 && <Divider />}
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
          <Box
            sx={{
              p: { xs: 1, md: 1.5 },
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f5f5f5" }
            }}
            onClick={() => setSensorsExpanded(!sensorsExpanded)}
          >
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: { xs: "13px", md: "14px" }, fontWeight: "600" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                <Iconify icon="mdi:gauge" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                Sensors
              </Box>
              <Iconify
                icon={sensorsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                sx={{ transition: "transform 0.2s" }}
                width={{ xs: 14, md: 16 }}
                height={{ xs: 14, md: 16 }}
              />
            </Typography>
          </Box>
          {sensorsExpanded && (
            <Box sx={{ p: { xs: 0.5, md: 1 } }}>
              <Stack spacing={0}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Speed</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "11px", md: "12px" },
                      color: device.velocity && overSpeed(device) ? "#ff5631" : "#4CAF50"
                    }}
                  >
                    {device.velocity ? `${device.velocity.toFixed(1)} mph` : "0 mph"}
                  </Typography>
                </Box>

                {eldEngineImmobilizer && eldEngineImmobilizer.speed !== undefined && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>ELD Speed</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                      {eldEngineImmobilizer.speed} mph
                    </Typography>
                  </Box>
                )}

                {device.externalIdentifier && !device.externalIdentifier.startsWith("2|") && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>Battery</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px", color: device.battery > 20 ? "#4CAF50" : "#ff5631" }}>
                      {device.battery}%
                    </Typography>
                  </Box>
                )}

                {eldEngineImmobilizer?.battery && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>ELD Battery</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                      {eldEngineImmobilizer.battery}
                    </Typography>
                  </Box>
                )}

                {device.direction !== null && device.direction !== undefined && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>Direction</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                      {device.direction}°
                    </Typography>
                  </Box>
                )}

                {eldEngineImmobilizer && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>GPS Status</Typography>
                    <Chip
                      label={eldEngineImmobilizer.gpsFixed ? "Fixed" : "Not Fixed"}
                      color={eldEngineImmobilizer.gpsFixed ? "success" : "warning"}
                      size="small"
                      sx={{ fontSize: "10px", height: "18px" }}
                    />
                  </Box>
                )}

                {eldEngineImmobilizer && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>Power</Typography>
                    <Chip
                      label={eldEngineImmobilizer.powerConnected ? "On" : "Off"}
                      color={eldEngineImmobilizer.powerConnected ? "success" : "error"}
                      size="small"
                      sx={{ fontSize: "10px", height: "18px" }}
                    />
                  </Box>
                )}

                {isTruck && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>Odometer {device.origin ? `(${getLabelFromOrigin(device.origin)})` : ""}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    {device.lastOdometer.toFixed(2)} mi
                  </Typography>
                </Box>
              )}

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>Last Ping</Typography>
                  <Typography variant="body2" sx={{ fontSize: "11px" }} color="text.secondary">
                    {formatDatetimeForPreview(device.lastPing, timezone)}
                  </Typography>
                </Box>
            </Stack>
          </Box>
        )}
      </Box>

        <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
          <Box
            sx={{
              p: { xs: 1, md: 1.5 },
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f5f5f5" }
            }}
            onClick={() => setDetailsExpanded(!detailsExpanded)}
          >
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: { xs: "13px", md: "14px" }, fontWeight: "600" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                <Iconify icon="mdi:clipboard-text" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                Details
              </Box>
              <Iconify
                icon={detailsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                sx={{ transition: "transform 0.2s" }}
                width={{ xs: 14, md: 16 }}
                height={{ xs: 14, md: 16 }}
              />
            </Typography>
          </Box>
          {detailsExpanded && (
            <Box sx={{ p: 1 }}>
              <Stack spacing={0}>
                <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                    External Identifier
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px", wordBreak: "break-all" }}>
                    {device.externalIdentifier || "N/A"}
                  </Typography>
                </Box>

                {vehicleVin && (
                  <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                      Vehicle VIN
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "monospace", fontSize: "11px", wordBreak: "break-all" }}>
                      {vehicleVin}
                    </Typography>
                  </Box>
                )}

                {eldEngineImmobilizer?.simId && (
                  <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                      SIM ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "11px", wordBreak: "break-all" }}>
                      {eldEngineImmobilizer.simId}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ py: 0.5, px: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                    Device Type
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "12px" }}>
                    {device.deviceType}
                  </Typography>
                </Box>

              {eldEngineImmobilizer?.dateActivated && (
                <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary"  sx={{ fontSize: "11px" }}>
                    Date Activated
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: "12px" }}>
                    {formatDatetimeForPreview(eldEngineImmobilizer.dateActivated, timezone)}
                  </Typography>
                </Box>
              )}

              <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "10px", md: "11px" } }}>
                  Current Location
                </Typography>
                <Typography variant="body1" sx={{ fontSize: { xs: "11px", md: "12px" } }}>
                  {device.location || "Unknown"}
                </Typography>
              </Box>

              <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "10px", md: "11px" } }}>
                  Device Coordinates
                </Typography>
                <Link
                  href={`https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: "10px", md: "11px" },
                    textDecoration: "none",
                    color: "blue",
                    "&:hover": {
                      textDecoration: "underline"
                    }
                  }}
                >
                  {device.lastPosLat}, {device.lastPosLong}
                </Link>
              </Box>

              {eldEngineImmobilizer && eldEngineImmobilizer.posLat && eldEngineImmobilizer.posLong &&
               (eldEngineImmobilizer.posLat !== device.lastPosLat || eldEngineImmobilizer.posLong !== device.lastPosLong) && (
                <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "10px", md: "11px" } }}>
                    ELD Coordinates
                  </Typography>
                    <Link
                  href={`https://maps.google.com/?q=${eldEngineImmobilizer.posLat},${eldEngineImmobilizer.posLong}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: "10px", md: "11px" },
                    textDecoration: "none",
                    color: "blue",
                    "&:hover": {
                      textDecoration: "underline"
                    }
                  }}
                >
                  {eldEngineImmobilizer.posLat}, {eldEngineImmobilizer.posLong}
                </Link>
                </Box>
              )}

              {device.lastDormancyReported && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Dormancy Reported
                  </Typography>
                  <Typography variant="body1">
                    {formatDatetimeForPreview(device.lastDormancyReported, timezone)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Box>

      {isTruck && (
         <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
          <Box
            sx={{
              p: { xs: 1, md: 1.5 },
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f5f5f5" }
            }}
            onClick={() => setDiagnosticsExpanded(!diagnosticsExpanded)}
          >
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: { xs: "13px", md: "14px" }, fontWeight: "600" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                <Iconify icon="mdi:wrench" width={{ xs: 14, md: 16 }} height={{ xs: 14, md: 16 }} />
                Diagnostics
              </Box>
              <Iconify
                icon={diagnosticsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                sx={{ transition: "transform 0.2s" }}
                width={{ xs: 14, md: 16 }}
                height={{ xs: 14, md: 16 }}
              />
            </Typography>
          </Box>
          {diagnosticsExpanded && (
            <Box sx={{ p: { xs: 0.5, md: 1 } }}>
              <Stack spacing={0}>
              {currentVehicleStats ? (
                <>
                  {currentVehicleStats.coolantLevelPercent > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Coolant Level</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                        {formatAtMost2Decimals(currentVehicleStats.coolantLevelPercent)}%
                      </Typography>
                    </Box>
                  )}

                  {currentVehicleStats.coolantTemperature > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Coolant Temperature</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                        {formatAtMost2Decimals(currentVehicleStats.coolantTemperature)}°C
                      </Typography>
                    </Box>
                  )}

                  {currentVehicleStats.fuelLevelPercent > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Fuel Level</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                        {formatAtMost2Decimals(currentVehicleStats.fuelLevelPercent)}%
                      </Typography>
                    </Box>
                  )}

                  {currentVehicleStats.oilLevelPercent > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Oil Level</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                        {formatAtMost2Decimals(currentVehicleStats.oilLevelPercent)}%
                      </Typography>
                    </Box>
                  )}

                  {currentVehicleStats.oilTemperature > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" } }}>Oil Temperature</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, fontWeight: "bold" }}>
                        {formatAtMost2Decimals(currentVehicleStats.oilTemperature)}°C
                      </Typography>
                    </Box>
                  )}

                  {currentVehicleStats.dtcCodes?.length > 0 && (
                    <Box sx={{ py: { xs: 0.25, md: 0.5 }, px: { xs: 0.5, md: 1 }, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: "11px", md: "12px" }, mb: 0.5 }}>Diagnostic Codes</Typography>
                      <Violations violations={formatDtc(currentVehicleStats.dtcCodes)} />
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ py: { xs: 1, md: 1.5 }, px: { xs: 0.5, md: 1 }, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "11px", md: "12px" } }}>
                    No vehicle statistics available
                  </Typography>
                </Box>
              )}
              </Stack>
            </Box>
          )}
        </Box>
      )}
        <Box sx={{ height: "16px" }} />
      </Box>
    </Box>
  );
}